import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { Store } from '@ngrx/store';
import {
  CartControlExtension,
  CheckoutExtensionUI,
  ExtensionComponentType,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { generateRandomString } from '@utils/string-utils';
import { RestrictionRules, SubscriptionSelectorType, TRANSPARENT_APPEARANCE } from './config';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopService } from '@app/services/shops/shop.service';
import * as CheckoutActions from '@app/pages/checkout-app/checkout-store/actions/checkout.actions';
import { ViewMode } from '../../../models';
import { CartControlFormGroup, ExceptionFormGroup } from './components/cart-control-form/cart-control-form.component';
import { createCartControlExceptionFormGroup } from './exception-form.factory';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';

export enum RemoveItemsType {
  ALL_ITEMS = 'all_items',
  WITHOUT_UPSELLS = 'without_upsells',
}

@Component({
  selector: 'cart-control',
  templateUrl: './cart-control.component.html',
  standalone: false,
})
export class CartControlComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;

  formGroup: FormGroup<CartControlFormGroup>;
  displayRules = [...RestrictionRules];
  displayRulesEntities;
  shopCurrencySymbol: string;

  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
    private extensionsService: ExtensionsService,
  ) {}

  // PDQ-7681 — tear down destroy$ explicitly (NOT via the @onDestroyComplete decorator, which both
  // no-ops when a component lacks its own ngOnDestroy AND calls the wrapped ngOnDestroy with a broken
  // `this`). Without this teardown, destroy$ never completes, so the save$ subscription in ngOnInit
  // leaks across navigation: every reopen of the editor adds another live handler, and one Save then
  // fans out into many racing PATCH requests (each with its own stale form snapshot). Completing
  // destroy$ here tears the subscription down, so exactly one handler (the live instance) saves per click.
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.CART_CONTROL);

    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.displayRulesEntities = this.displayRules.map((r) => r.entity);

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension.template, extension.usageToken);
      } else {
        this.initForm();
      }
    });
  }

  dispatchExampleViewValues(values: any) {
    if (!values) {
      return;
    }

    const template = {
      internalName: values.internalName,
      internalDescription: values.internalDescription,
      configurations: values.configurations,
    };

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.CART_CONTROL]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string) {
    this.formGroup = new FormGroup<CartControlFormGroup>({
      usageToken: new FormControl<string>(usageToken ?? 'cart_control_' + generateRandomString(12)),

      internalName: new FormControl<string>(loadedValues?.internalName ?? 'Cart Control', Validators.required),
      internalDescription: new FormControl<string>(
        loadedValues?.internalDescription ?? loadedValues?.description ?? loadedValues?.title ?? 'Cart Control',
      ),

      configurations: new FormGroup({
        buttonLabel: new FormControl<string>(loadedValues?.configurations?.buttonLabel ?? 'Edit', Validators.required),
        buttonAppearance: new FormControl<string>(
          loadedValues?.configurations?.buttonAppearance ?? CheckoutExtensionUI.Appearance.ACCENT,
        ),
        changeVariant: new FormControl<boolean>(loadedValues?.configurations?.changeVariant ?? false),
        editQuantity: new FormControl<boolean>(loadedValues?.configurations?.editQuantity ?? false),
        removeItems: new FormControl<boolean>(loadedValues?.configurations?.removeItems ?? false),
        showSubscriptionSelector: new FormControl<boolean>(
          loadedValues?.configurations?.showSubscriptionSelector ?? false,
        ),
        subscriptionSelectorType: new FormControl<SubscriptionSelectorType>(
          loadedValues?.configurations?.subscriptionSelectorType ?? SubscriptionSelectorType.DEFAULT_SELECTOR,
        ),
        upgradeButtonLabel: new FormControl<string>(
          loadedValues?.configurations?.upgradeButtonLabel ?? 'Upgrade to {subscription_group}',
        ),
        upgradeButtonAppearance: new FormControl<string>(
          this.resolveUpgradeButtonAppearance(loadedValues?.configurations),
        ),
        upgradeButtonTextColor: new FormControl<string>(
          this.resolveUpgradeButtonTextColor(loadedValues?.configurations),
        ),
        exceptions: this.buildExceptionsFormArray(loadedValues?.configurations?.exceptions),
      }),

      displayRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.displayRules ?? [],
        this.displayRules,
      ) as FormArray<FormGroup<any>>,
    });

    const upgradeButtonLabelControl = this.formGroup.get('configurations.upgradeButtonLabel');

    const updateUpgradeButtonLabelValidation = () => {
      const showSubscriptionSelector = this.formGroup.get('configurations.showSubscriptionSelector')?.value;
      const subscriptionType = this.formGroup.get('configurations.subscriptionSelectorType')?.value;

      if (showSubscriptionSelector && subscriptionType === SubscriptionSelectorType.UPGRADE) {
        upgradeButtonLabelControl?.setValidators(Validators.required);
      } else {
        upgradeButtonLabelControl?.clearValidators();
      }
      upgradeButtonLabelControl?.updateValueAndValidity();
    };

    updateUpgradeButtonLabelValidation();

    const subscriptionSelectorTypeChanges$ = this.formGroup.get(
      'configurations.subscriptionSelectorType',
    )?.valueChanges;
    const showSubscriptionSelectorChanges$ = this.formGroup.get(
      'configurations.showSubscriptionSelector',
    )?.valueChanges;

    if (subscriptionSelectorTypeChanges$ && showSubscriptionSelectorChanges$) {
      merge(subscriptionSelectorTypeChanges$, showSubscriptionSelectorChanges$)
        .pipe(takeUntil(this.destroy$))
        .subscribe(updateUpgradeButtonLabelValidation);
    }

    this.dispatchExampleViewValues(this.formGroup.value);
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
      // PDQ-7681 — recompute Save-enabled on value changes too. Adding/removing an exception changes
      // the form VALUE without necessarily flipping the aggregate STATUS (empty exceptions is a valid
      // state), so statusChanges alone leaves the Save button stale after the last exception is removed.
      this.extensionsService.setFormValid(this.isFormValidForSave());
    });

    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.isFormValidForSave());
    });
    this.extensionsService.setFormValid(this.isFormValidForSave());
  }

  // PDQ-7681 — an untouched starter/blank exception is a validator-free placeholder (see
  // rules-dynamic-form's showEmptyRowByDefault seeding), so it doesn't invalidate the form; it's
  // pruned on save via pruneEmptyExceptions(). A half-built exception (entity picked, value empty)
  // carries its own required validators and correctly blocks Save. So aggregate validity is honest.
  private isFormValidForSave(): boolean {
    return this.formGroup.valid;
  }

  // PDQ-7681 — strip incomplete data from the LIVE form before saving: (1) condition rows with no
  // entity selected (e.g. the validator-free starter row left empty beside a real rule), then
  // (2) any exception left with zero conditions (an entity-less exception would match EVERY cart
  // line and override global for the whole cart). Must only run once the save is going ahead — see
  // handleSave — so a blocked save never mutates the live form.
  private pruneEmptyExceptions(): void {
    const exceptions = this.formGroup.get('configurations.exceptions') as FormArray<FormGroup<ExceptionFormGroup>>;
    if (!exceptions?.length) {
      return;
    }
    for (let i = exceptions.length - 1; i >= 0; i--) {
      const conditions = exceptions.at(i).get('conditions') as FormArray;
      for (let j = (conditions?.length ?? 0) - 1; j >= 0; j--) {
        if (!conditions.at(j).get('entity')?.value) {
          conditions.removeAt(j);
        }
      }
      if (!conditions?.length) {
        exceptions.removeAt(i);
      }
    }
  }

  // PDQ-7681 — build the FormArray of rule-based exceptions from the persisted
  // template.configurations.exceptions (empty when none / not yet configured).
  private buildExceptionsFormArray(
    exceptions?: CartControlExtension.Exception[],
  ): FormArray<FormGroup<ExceptionFormGroup>> {
    return new FormArray<FormGroup<ExceptionFormGroup>>(
      (exceptions ?? []).map((exception) =>
        createCartControlExceptionFormGroup(this.rulesDynamicFormService, exception),
      ),
    );
  }

  private readonly validUpgradeAppearances = [
    TRANSPARENT_APPEARANCE,
    CheckoutExtensionUI.Appearance.BASE,
    CheckoutExtensionUI.Appearance.MONOCHROME,
    CheckoutExtensionUI.Appearance.CRITICAL,
  ];

  private resolveUpgradeButtonAppearance(configurations?: any): string {
    const value = configurations?.upgradeButtonAppearance;
    const textColor = configurations?.upgradeButtonTextColor;
    if (!value || !this.validUpgradeAppearances.includes(value)) {
      return TRANSPARENT_APPEARANCE;
    }
    if (!textColor) {
      return TRANSPARENT_APPEARANCE;
    }
    return value;
  }

  private resolveUpgradeButtonTextColor(configurations?: any): string {
    if (configurations?.upgradeButtonTextColor) {
      return configurations.upgradeButtonTextColor;
    }
    const oldAppearance = configurations?.upgradeButtonAppearance;
    if (oldAppearance) {
      return oldAppearance;
    }
    return CheckoutExtensionUI.Appearance.ACCENT;
  }

  // Fields that can have validation errors:
  // - internalName: required
  // - configurations.buttonLabel: required
  // - configurations.upgradeButtonLabel: required (conditionally, when showSubscriptionSelector is true AND subscriptionSelectorType is UPGRADE)
  handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    // Save is going ahead — now drop incomplete exceptions/rows (untouched starter, empty rows) from
    // the live form. Doing this AFTER the invalid check means a blocked save never mutates the UI.
    // Empty/starter exceptions don't affect validity (the starter row is validator-free), so pruning
    // here can't turn a valid form invalid.
    this.pruneEmptyExceptions();
    const formValue = this.formGroup.value;
    const { usageToken, ...template } = formValue;
    const payload: ICheckoutExtensions = {
      template,
      usageToken,
      type: ExtensionComponentType.CART_CONTROL,
    };
    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
